// A right-click menu: what a DAW keeps under the second mouse button.
//
// Presentational. It is handed a point on the screen and a list of entries,
// and it calls `onSelect` and closes — what each entry does, and which ones a
// place on the timeline offers, are the caller's (ArrangementView asks the
// page). Built here rather than on a radix primitive because the menu opens at
// a point the caller computed (a lane, a beat), not on a trigger element.
//
// Behaves as the platform's own menus do: it opens at the pointer and flips
// back inside the window when it would run off an edge; Escape, a press
// anywhere else, a scroll, a resize or the window losing focus closes it; the
// arrow keys walk its enabled items, Home / End jump, Enter picks. Keys
// pressed in it stay in it — the page's ⌫ and arrows edit the selection.

import { useEffect, useLayoutEffect, useRef, useState, type ComponentType, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { Check } from "lucide-react";
import { cn } from "@web-shared/lib/utils";

export interface ContextMenuItem {
  kind?: "item";
  /** Stable within one menu: the React key, and what a test finds it by. */
  id: string;
  label: string;
  icon?: ComponentType<{ className?: string }>;
  /** The key that does the same thing, shown at the right — the menu is where
   *  a pointer learns the shortcuts. */
  shortcut?: string;
  disabled?: boolean;
  /** Loses work: drawn in the danger colour. */
  danger?: boolean;
  /** A toggle's state (mute, solo, arm): a tick in the icon's place. */
  checked?: boolean;
  onSelect: () => void;
}

export type ContextMenuEntry = ContextMenuItem | { kind: "separator" } | { kind: "label"; label: string };

/** The entries as they will be drawn: no separator first, last or doubled, so a
 *  caller can append groups without minding which of them came out empty. */
export function tidyEntries(entries: readonly ContextMenuEntry[]): ContextMenuEntry[] {
  const out: ContextMenuEntry[] = [];
  for (const entry of entries) {
    if (entry.kind === "separator" && (out.length === 0 || out[out.length - 1]!.kind === "separator" || out[out.length - 1]!.kind === "label")) continue;
    out.push(entry);
  }
  while (out.length > 0 && out[out.length - 1]!.kind === "separator") out.pop();
  return out;
}

/** Keep clear of the window's edge by this much. */
const EDGE = 6;

function isItem(entry: ContextMenuEntry): entry is ContextMenuItem {
  return entry.kind === undefined || entry.kind === "item";
}

export function ContextMenu({
  x,
  y,
  entries,
  onClose,
  label = "Actions",
  touch = false,
}: {
  /** Client coordinates — where the pointer was. */
  x: number;
  y: number;
  entries: readonly ContextMenuEntry[];
  onClose: () => void;
  /** The menu's accessible name. */
  label?: string;
  /** Opened by a finger: 44px rows a thumb can hit, a little off the
   *  point pressed so the finger is not covering the first of them. */
  touch?: boolean;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const shown = tidyEntries(entries);

  // Measure, then place: to the pointer's right and below it, flipped to its
  // left / above when that would leave the window, and clamped in either way.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const off = touch ? 12 : 0;
    let left = x + off + w + EDGE > vw ? x - off - w : x + off;
    let top = y + off + h + EDGE > vh ? y - off - h : y + off;
    left = Math.max(EDGE, Math.min(left, vw - w - EDGE));
    top = Math.max(EDGE, Math.min(top, vh - h - EDGE));
    setPos({ left, top });
  }, [x, y, shown.length, touch]);

  // Focus moves into the menu, so the keys below are its; it goes back to
  // wherever it was when the menu closes.
  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    const el = ref.current;
    el?.focus({ preventScroll: true });
    return () => {
      // Unless the item picked put it somewhere on purpose (Rename focuses a
      // name field): then it stays there.
      const now = document.activeElement;
      const stray = now === null || now === document.body || (el !== null && el.contains(now));
      if (stray && before && document.contains(before)) before.focus({ preventScroll: true });
    };
  }, []);

  useEffect(() => {
    const close = () => closeRef.current();
    const press = (e: PointerEvent) => {
      if (ref.current && e.target instanceof Node && ref.current.contains(e.target)) return;
      close();
    };
    const scroll = (e: Event) => {
      if (ref.current && e.target instanceof Node && ref.current.contains(e.target)) return;
      close();
    };
    // A second right-click elsewhere: this menu goes, and the next one opens
    // where that click was (its own handler, which runs after this).
    const context = (e: MouseEvent) => {
      if (ref.current && e.target instanceof Node && ref.current.contains(e.target)) {
        e.preventDefault();
        return;
      }
      close();
    };
    window.addEventListener("pointerdown", press, true);
    window.addEventListener("contextmenu", context, true);
    window.addEventListener("scroll", scroll, true);
    window.addEventListener("resize", close);
    window.addEventListener("blur", close);
    return () => {
      window.removeEventListener("pointerdown", press, true);
      window.removeEventListener("contextmenu", context, true);
      window.removeEventListener("scroll", scroll, true);
      window.removeEventListener("resize", close);
      window.removeEventListener("blur", close);
    };
  }, []);

  const items = (): HTMLButtonElement[] =>
    Array.from(ref.current?.querySelectorAll<HTMLButtonElement>('[role^="menuitem"]:not(:disabled)') ?? []);
  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    // Nothing typed in the menu reaches the page.
    e.stopPropagation();
    const list = items();
    const at = list.indexOf(document.activeElement as HTMLButtonElement);
    const go = (i: number) => {
      e.preventDefault();
      list[(i + list.length) % list.length]?.focus();
    };
    if (e.key === "Escape" || e.key === "Tab") {
      e.preventDefault();
      onClose();
    } else if (list.length === 0) {
      return;
    } else if (e.key === "ArrowDown") go(at + 1);
    else if (e.key === "ArrowUp") go(at < 0 ? -1 : at - 1);
    else if (e.key === "Home") go(0);
    else if (e.key === "End") go(-1);
  };

  if (shown.length === 0 || typeof document === "undefined") return null;
  return createPortal(
    <div
      ref={ref}
      role="menu"
      aria-label={label}
      tabIndex={-1}
      data-context-menu
      onKeyDown={onKeyDown}
      // Its own: a right-click on the menu is not a right-click on the page.
      onContextMenu={(e) => e.preventDefault()}
      className={cn(
        "fixed z-[100] min-w-[220px] max-w-[min(300px,calc(100vw-12px))] select-none overflow-hidden rounded-lg border border-panelline bg-raised p-1 text-xs text-foreground shadow-[0_12px_32px_rgba(0,0,0,0.55)] outline-none",
        touch && "min-w-[240px] text-sm",
        "motion-safe:transition-[opacity,transform] motion-safe:duration-75",
        pos ? "opacity-100" : "pointer-events-none opacity-0 motion-safe:scale-[0.98]",
      )}
      style={{ left: pos?.left ?? x, top: pos?.top ?? y }}
    >
      {shown.map((entry, i) => {
        if (entry.kind === "separator") return <div key={`sep-${i}`} role="separator" className="mx-1 my-1 h-px bg-panelline" />;
        if (entry.kind === "label") {
          return (
            <div
              key={`label-${i}`}
              className={cn("truncate pt-1.5 pb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground", touch ? "px-3" : "px-2")}
            >
              {entry.label}
            </div>
          );
        }
        if (!isItem(entry)) return null;
        const Icon = entry.icon;
        const toggle = entry.checked !== undefined;
        return (
          <button
            key={entry.id}
            type="button"
            role={toggle ? "menuitemcheckbox" : "menuitem"}
            aria-checked={toggle ? entry.checked : undefined}
            data-menu-item={entry.id}
            disabled={entry.disabled}
            onClick={() => {
              onClose();
              entry.onSelect();
            }}
            // The pointer and the keys share one highlight: hovering an item
            // focuses it, so the arrows carry on from there.
            onPointerMove={(e) => {
              if (document.activeElement !== e.currentTarget) e.currentTarget.focus({ preventScroll: true });
            }}
            className={cn(
              "flex w-full items-center gap-2 rounded-md px-2 text-left outline-none",
              touch ? "h-11 gap-3 px-3" : "h-7",
              "focus:bg-white/[0.07] disabled:pointer-events-none disabled:opacity-35",
              entry.danger && "text-[var(--removed)] focus:bg-[color-mix(in_srgb,var(--removed)_14%,transparent)]",
            )}
          >
            <span className="flex size-4 shrink-0 items-center justify-center text-muted-foreground" aria-hidden>
              {toggle ? (
                entry.checked ? <Check className="size-3.5 text-primary" /> : Icon ? <Icon className="size-3.5" /> : null
              ) : Icon ? (
                <Icon className={cn("size-3.5", entry.danger && "text-[var(--removed)]")} />
              ) : null}
            </span>
            <span className="min-w-0 flex-1 truncate">{entry.label}</span>
            {/* No keyboard under a thumb: the keys would be noise. */}
            {entry.shortcut && !touch && <kbd className="ml-3 shrink-0 font-sans text-[10px] tracking-wide text-muted-foreground tabular">{entry.shortcut}</kbd>}
          </button>
        );
      })}
    </div>,
    document.body,
  );
}
